import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Injector,
  AfterViewInit,
  ElementRef
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ACLService } from '@delon/acl';
import { _HttpClient } from '@delon/theme';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';

import { EInvoiceService } from '../../../core/services/e-invoice.service';
import { AutoCodeService } from '../../services/auto-code.service';

export interface InlineAddConfig {
  modelType: string;
  fields: InlineAddField[];
  apiEndpoint: string;
  title?: string;
  submitButtonText?: string;
  cancelButtonText?: string;
  fieldMapping?: { [key: string]: string };
  selectionConfig?: {
    targetField: string;
    valueField: string;
    labelField: string;
  };
  hiddenData?: { [key: string]: any };
  showMoreDetails?: boolean;
}

export interface InlineAddField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'number' | 'select' | 'textarea' | 'radio-group' | 'switch';
  required?: boolean;
  maxLength?: number;
  isInstantData?: boolean;
  options?: Array<{ label: string; value: any; data?: any }>;
  placeholder?: string;
  validation?: any[];
  size?: 'full' | 'half';
  defaultValue?: any;
  searchConfig?: {
    url: string;
    valueField?: string;
    labelField?: string;
    searchFields?: string[];
    defaultFilter?: string;
    orderby?: string;
  };
}

interface SelectState {
  list: any[];
  loading: boolean;
  page: number;
  hasMore: boolean;
  search: string;
  completed?: boolean;
}

@Component({
  selector: 'app-inline-add-panel',
  templateUrl: './inline-add-panel.component.html',
  styleUrls: ['./inline-add-panel.component.less'],
  standalone: false,
  providers: [
    {
      provide: AutoCodeService,
      useFactory: (http: _HttpClient) => new AutoCodeService(http),
      deps: [_HttpClient]
    },
    {
      provide: EInvoiceService,
      useFactory: (http: _HttpClient, acl: ACLService, injector: Injector) =>
        new EInvoiceService(http, acl, injector),
      deps: [_HttpClient, ACLService, Injector]
    }
  ]
})
export class InlineAddPanelComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() config!: InlineAddConfig;
  @Input()
  set visible(value: boolean) {
    this._visible = value;
    if (value) {
      // Panel is now visible, initialize auto-code after a delay
      setTimeout(() => {
        this.triggerAutoCodeGeneration();
        // Ensure any provided initial data is applied when panel becomes visible
        if (this.formGroup && this._initialData) {
          this.formGroup.patchValue(this._initialData);
        }
        // Auto-position the panel to prevent overflow
        this.autoPositionPanel();
        
        // Force form validation update after panel becomes visible
        setTimeout(() => {
          if (this.formGroup) {
            // Mark all controls as touched to trigger validation
            Object.keys(this.formGroup.controls).forEach(key => {
              const control = this.formGroup.get(key);
              if (control && control.value) {
                control.markAsTouched();
                control.markAsDirty();
                control.updateValueAndValidity();
              }
            });
            // Force change detection to update the UI
            this.cdr.detectChanges();
          }
        }, 200);
        
        // Force visibility and positioning
        setTimeout(() => {
          const panelElement = this.elementRef.nativeElement.querySelector('.inline-add-panel');
          if (panelElement) {
            panelElement.style.display = 'block';
            panelElement.style.visibility = 'visible';
            panelElement.style.opacity = '1';
            panelElement.style.zIndex = '15';
            
            // Only apply form field fixes when in drawer context
            if (this.isInsideDrawer()) {
              panelElement.classList.add('drawer-context');
              this.ensureFormFieldsDisplay(panelElement);
            }
            
            console.log('Panel visibility forced:', {
              display: panelElement.style.display,
              visibility: panelElement.style.visibility,
              opacity: panelElement.style.opacity,
              zIndex: panelElement.style.zIndex,
              isInDrawer: this.isInsideDrawer()
            });
          }
        }, 100);
      }, 300);
    }
  }
  get visible(): boolean {
    return this._visible;
  }
  private _visible = false;
  private _initialData: any;
  @Input() set initialData(value: any) {
    this._initialData = value;
    if (this.formGroup && value) {
      this.formGroup.patchValue(value);
    }
  }
  get initialData(): any {
    return this._initialData;
  }
  @Input() isOverlay: boolean = false;

  @Output() readonly saved = new EventEmitter<any>();
  @Output() readonly cancelled = new EventEmitter<void>();
  @Output() readonly visibleChange = new EventEmitter<boolean>();
  @Output() readonly moreDetails = new EventEmitter<void>();
  @Output() readonly fieldChange = new EventEmitter<{
    fieldName: string;
    value: any;
  }>();
  @Output() readonly itemCreated = new EventEmitter<{
    item: any;
    selectionConfig?: {
      targetField: string;
      valueField: string;
      labelField: string;
    };
  }>();

  @ViewChild('form') form!: FormGroup;
  

  formGroup!: FormGroup;
  loading = false;
  private destroy$ = new Subject<void>();
  private selectedControlAccount: any = null;
  
  // Select states for server-side search
  selectStates: { [key: string]: SelectState } = {};
  searchSubjects: { [key: string]: Subject<string> } = {};

  nzFilterOption = () => true;

  constructor(
    private fb: FormBuilder,
    private http: _HttpClient,
    private message: NzMessageService,
    private cdr: ChangeDetectorRef,
    private autoCodeService: AutoCodeService,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.initForm();
    
    // Listen for window resize to reposition panel if needed
    window.addEventListener('resize', this.handleWindowResize.bind(this));

    // Initialize select states
    this.config.fields.forEach(field => {
      if (field.type === 'select' && field.searchConfig) {
        this.selectStates[field.name] = {
          list: [],
          loading: false,
          page: 0,
          hasMore: true,
          search: '',
          completed: false
        };
        
        // Initialize search subject
        this.searchSubjects[field.name] = new Subject<string>();
        this.searchSubjects[field.name].pipe(
          takeUntil(this.destroy$),
          debounceTime(300),
          distinctUntilChanged()
        ).subscribe(value => {
          this.loadSelectOptions(field, value, true);
        });

        // Load initial options
        this.loadSelectOptions(field, '', true);
      }
    });
  }

  ngAfterViewInit(): void {
    // Initialize auto-code when panel becomes visible
    if (this.visible) {
      setTimeout(() => {
        this.triggerAutoCodeGeneration();
      }, 500);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Complete all search subjects
    Object.values(this.searchSubjects).forEach(subject => subject.complete());

    // Remove window resize listener
    window.removeEventListener('resize', this.handleWindowResize.bind(this));
  }

  private initForm(): void {
    const group: any = {};

    this.config.fields.forEach(field => {
      const validators = [];

      if (field.required) {
        validators.push(Validators.required);
      }

      if (field.type === 'email') {
        validators.push(Validators.email);
      }

      if (field.maxLength) {
        validators.push(Validators.maxLength(field.maxLength));
      }

      if (field.validation) {
        validators.push(...field.validation);
      }

      // Initialize fields with defaultValue if provided, otherwise use null for optional fields or empty string for required fields
      let initialValue;
      if (field.defaultValue !== undefined) {
        initialValue = field.defaultValue;
      } else {
        initialValue = field.required ? '' : null;
      }
      group[field.name] = [initialValue, validators];
    });

    this.formGroup = this.fb.group(group);

    if (this._initialData) {
      this.formGroup.patchValue(this._initialData);
    }
  }

  changeInstantData(instantData: string): void {
    let instantFieldName = this.config.fields.find(f => f.isInstantData)?.name;
    console.log(instantData);
    console.log(instantFieldName);
    if (instantData && instantFieldName) {
      const control = this.formGroup.get(instantFieldName);
      if (control) {
        control.setValue(instantData);
        control.markAsTouched();
        control.markAsDirty();
        // Trigger validation to ensure form state is updated
        control.updateValueAndValidity();
      }
    }
  }

  onCancel(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.cancelled.emit();

    // Clean up drawer context class
    const panelElement = this.elementRef.nativeElement.querySelector('.inline-add-panel');
    if (panelElement) {
      panelElement.classList.remove('drawer-context');
    }

    // Reset form with proper null values for optional fields
    const resetValues: any = {};
    this.config.fields.forEach(field => {
      resetValues[field.name] = field.required ? '' : null;
    });
    this.formGroup.reset(resetValues);
  }

  onMoreDetails(): void {
    this.moreDetails.emit();
  }

  shouldShowMoreDetailsButton(): boolean {
    // If showMoreDetails is explicitly set in config, use that value
    if (this.config.showMoreDetails !== undefined) {
      return this.config.showMoreDetails;
    }
    
    // Default behavior: show more details button for all model types
    return true;
  }

  onSubmit(): void {
    if (this.formGroup.valid) {
      this.loading = true;
      const formData = this.formGroup.value;

      // Apply field mapping if configured
      const mappedData = this.applyFieldMapping(formData);

      // Apply data transformation if configured
      const transformedData = this.applyDataTransformation(mappedData);

      // Merge hidden data if configured
      const finalData = this.config.hiddenData
        ? { ...transformedData, ...this.config.hiddenData }
        : transformedData;

      this.http
        .post(this.config.apiEndpoint, finalData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            this.message.success(
              `${this.config.title || 'Item'} created successfully`
            );
            this.saved.emit(response);

            // Emit item created event with selection configuration
            this.itemCreated.emit({
              item: response,
              selectionConfig: this.config.selectionConfig
            });

            this.visible = false;
            this.visibleChange.emit(false);

            // Clean up drawer context class
            const panelElement = this.elementRef.nativeElement.querySelector('.inline-add-panel');
            if (panelElement) {
              panelElement.classList.remove('drawer-context');
            }

            // Reset form with proper null values for optional fields
            const resetValues: any = {};
            this.config.fields.forEach(field => {
              resetValues[field.name] = field.required ? '' : null;
            });
            this.formGroup.reset(resetValues);

            this.loading = false;
          },
          error: error => {
            this.message.error('Failed to create item. Please try again.');
            this.loading = false;
          }
        });
    } else {
      this.markFormGroupTouched();
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.get(key);
      control?.markAsTouched();
    });
  }

  getFieldError(fieldName: string): string {
    const control = this.formGroup.get(fieldName);
    if (control?.errors && control.touched) {
      if (control.errors['required']) {
        return `${this.getFieldLabel(fieldName)} is required`;
      }
      if (control.errors['email']) {
        return 'Please enter a valid email address';
      }
      if (control.errors['maxlength']) {
        return `${this.getFieldLabel(fieldName)} is too long`;
      }
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const field = this.config.fields.find(f => f.name === fieldName);
    return field?.label || fieldName;
  }

  getFieldType(field: InlineAddField): string {
    switch (field.type) {
      case 'email':
        return 'email';
      case 'number':
        return 'number';
      default:
        return 'text';
    }
  }

  getFieldSizeClass(field: InlineAddField): string {
    return field.size === 'full' ? 'full-width' : 'half-width';
  }

  onFieldChange(fieldName: string, value: any): void {
    // Emit field change event for parent components to handle
    this.fieldChange.emit({ fieldName, value });

    // Handle specific field changes for auto-code
    if (fieldName === 'controlAccountId') {
      this.onControlAccountChange();
    }

    // Trigger auto-code generation when name or control account changes
    if (fieldName === 'name' || fieldName === 'controlAccountId') {
      setTimeout(() => {
        this.triggerAutoCodeGeneration();
      }, 200);
    }
  }

  getCompanyName(): string {
    return this.formGroup?.get('name')?.value || '';
  }

  getControlAccountCode(): string {
    const controlAccountId = this.formGroup?.get('controlAccountId')?.value;
    if (controlAccountId) {
      const controlAccountField = this.config.fields.find(
        f => f.name === 'controlAccountId'
      );
      const selectedOption = controlAccountField?.options?.find(
        opt => opt.value === controlAccountId
      );
      return selectedOption?.label?.split(' - ')[0] || '';
    }
    return '';
  }

  getSelectedControlAccount(): any {
    const controlAccountId = this.formGroup?.get('controlAccountId')?.value;
    if (controlAccountId) {
      const controlAccountField = this.config.fields.find(
        f => f.name === 'controlAccountId'
      );
      const selectedOption = controlAccountField?.options?.find(
        opt => opt.value === controlAccountId
      );
      if (selectedOption) {
        // Extract code from label (format: "CODE - Name")
        const labelParts = selectedOption.label.split(' - ');
        const code = labelParts[0] || '';
        const name = labelParts[1] || selectedOption.label;

        // Create control account object with code
        return {
          id: controlAccountId,
          code: code,
          name: name,
          ...selectedOption.data // Include any additional data from the option
        };
      }
    }
    return null;
  }

  // Method to trigger auto-code generation when control account changes
  onControlAccountChange(): void {
    // Store the selected control account object
    this.selectedControlAccount = this.getSelectedControlAccount();

    // Trigger auto-code generation for both customer and supplier
    this.triggerAutoCodeGeneration();

    // Force change detection to update auto-code component
    this.cdr.detectChanges();
  }

  private applyFieldMapping(data: any): any {
    const mappedData = { ...data };

    // Apply field mappings if configured
    if (this.config.fieldMapping) {
      Object.keys(this.config.fieldMapping).forEach(sourceField => {
        const targetField = this.config.fieldMapping![sourceField];
        if (mappedData.hasOwnProperty(sourceField)) {
          mappedData[targetField] = mappedData[sourceField];
          delete mappedData[sourceField];
        }
      });
    }

    // Handle nullable optional fields for supplier
    if (this.config.modelType === 'supplier') {
      // Convert empty strings and undefined values to null for optional fields
      const optionalFields = ['areaId', 'agentId', 'categoryId'];
      optionalFields.forEach(field => {
        if (mappedData.hasOwnProperty(field)) {
          const value = mappedData[field];
          if (value === '' || value === undefined || value === null) {
            mappedData[field] = null;
          }
        }
      });
    }

    // Add control account object for customer or supplier creation
    if (
      (this.config.modelType === 'customer' ||
        this.config.modelType === 'supplier') &&
      this.selectedControlAccount
    ) {
      mappedData.controlAccount = this.selectedControlAccount;
    }

    return mappedData;
  }

  private applyDataTransformation(data: any): any {
    const transformedData = { ...data };

    // Apply data transformations based on model type
    if (this.config.modelType === 'taxcode') {
      // Convert rate from percentage string to decimal number for tax codes
      if (transformedData.hasOwnProperty('rate')) {
        const rateValue = parseFloat(transformedData.rate) || 0;
        // Convert percentage to decimal (e.g., 32 -> 0.32)
        transformedData.rate = rateValue / 100;
      }
    } else if (this.config.modelType === 'wtaxcode') {
      // Convert rate from percentage string to decimal number for tax codes
      if (transformedData.hasOwnProperty('rate')) {
        const rateValue = parseFloat(transformedData.rate) || 0;
        // Convert percentage to decimal (e.g., 32 -> 0.32)
        transformedData.rate = rateValue / 100;
      }

      // Handle taxCategory mapping to booleans
      if (transformedData.hasOwnProperty('taxCategory')) {
        const category = Number(transformedData.taxCategory);
        transformedData.isIncomeTax = category === 1;
        transformedData.isWithholdingTax = category === 2;
        transformedData.isFinalWTax = category === 3;
        transformedData.isWithholdingVAT = category === 4;
      } else {
         // Default if taxCategory not present (though it should be based on config)
         transformedData.isIncomeTax = false;
         transformedData.isWithholdingTax = false;
         transformedData.isFinalWTax = false;
         transformedData.isWithholdingVAT = false;
      }

      // Handle Payable Account
      if (transformedData.hasOwnProperty('payableAccountId')) {
        // If payableAccountId is set, we might need to set payableAccount object as well if the backend requires it
        // Or if the backend only needs the ID.
        // Based on the user query, they showed a model with both payableAccount and payableAccountId
        // We have the ID from the form.
        
        // We also need to see if we have the full object in our options to populate payableAccount
        const payableAccountId = transformedData.payableAccountId;
        if (payableAccountId) {
            const payableAccountField = this.config.fields.find(f => f.name === 'payableAccountId');
            if (payableAccountField && payableAccountField.options) {
                // Also check static options if available
                const selectedOption = payableAccountField.options.find(opt => opt.value === payableAccountId);
                if (selectedOption && selectedOption.data) {
                    transformedData.payableAccount = selectedOption.data;
                }
            }
        }
      }

      // Handle Creditable Account
      if (transformedData.hasOwnProperty('creditableAccountId')) {
        const creditableAccountId = transformedData.creditableAccountId;
        if (creditableAccountId) {
            const creditableAccountField = this.config.fields.find(f => f.name === 'creditableAccountId');
            if (creditableAccountField && creditableAccountField.options) {
                // Also check static options if available
                const selectedOption = creditableAccountField.options.find(opt => opt.value === creditableAccountId);
                if (selectedOption && selectedOption.data) {
                    transformedData.creditableAccount = selectedOption.data;
                }
            }
        }
      }
    }

    return transformedData;
  }

  // Method to manually trigger auto-code generation
  triggerAutoCodeGeneration(): void {
    const companyName = this.getCompanyName();
    const controlAccount = this.getControlAccountCode();

    if (companyName && controlAccount) {
      // Force the auto-code component to generate a code
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 100);
    } else {
      // If we don't have the required data yet, set a placeholder
      const codeFieldName =
        this.config.modelType === 'customer' ? 'customerCode' : 'supplierCode';
      const currentValue = this.formGroup.get(codeFieldName)?.value;
      if (!currentValue || currentValue.trim() === '') {
        // Use AutoCodeService to generate the format ID
        this.generateAutoCodePlaceholder();
      }
    }
  }

  // Method to generate auto-code placeholder using AutoCodeService
  private generateAutoCodePlaceholder(): void {
    const codeFieldName =
      this.config.modelType === 'customer' ? 'customerCode' : 'supplierCode';
    const type = this.config.modelType === 'customer' ? 'AR' : 'AP';

    // Set up the auto-code service parameters for code generation
    const queryParams = {
      type: type,
      companyName: this.getCompanyName(),
      controlAccountCode: this.getControlAccountCode()
    };

    // Configure the auto-code service
    this.autoCodeService.onChange = (value: string) => {
      if (value && value.trim()) {
        this.formGroup.get(codeFieldName)?.setValue(value);
        this.formGroup.get(codeFieldName)?.markAsTouched();
        this.formGroup.get(codeFieldName)?.markAsDirty();
      }
    };

    // Trigger the auto-code generation
    this.autoCodeService.changeParams(queryParams);
  }

  /**
   * Automatically positions the panel to prevent overflow off the screen
   */
  private autoPositionPanel(): void {
    setTimeout(() => {
      const panelElement = this.elementRef.nativeElement.querySelector('.inline-add-panel');
      if (!panelElement) {
        console.warn('Inline add panel element not found');
        return;
      }

      // Find the parent select container to determine the dropdown position
      const selectContainer = this.findSelectContainer();
      if (!selectContainer) {
        console.warn('Select container not found, using fallback positioning');
        // Fallback: position relative to the panel's parent
        panelElement.style.position = 'absolute';
        panelElement.style.top = '100%';
        panelElement.style.left = '0';
        panelElement.style.right = '0';
        panelElement.style.zIndex = '15';
        return;
      }

      const selectRect = selectContainer.getBoundingClientRect();
      const panelRect = panelElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Calculate available space on both sides
      const spaceOnRight = viewportWidth - selectRect.left;
      const spaceOnLeft = selectRect.left;
      const panelWidth = Math.min(panelRect.width, 800); // Use max-width
      
      // Determine if we should position to the left or right
      let shouldPositionLeft = false;
      
      // If dropdown is in the right half of the screen, position panel to the left
      if (selectRect.left > viewportWidth / 2) {
        shouldPositionLeft = true;
      }
      
      // If there's not enough space on the right, position to the left
      if (spaceOnRight < panelWidth + 20) {
        shouldPositionLeft = true;
      }
      
      // Remove any existing positioning classes
      panelElement.classList.remove('positioned-left', 'positioned-center');
      
      // If there's not enough space on the left either, center it
      if (shouldPositionLeft && spaceOnLeft < panelWidth + 20) {
        // Center the panel
        panelElement.classList.add('positioned-center');
        panelElement.style.left = '50%';
        panelElement.style.right = 'auto';
        panelElement.style.transform = 'translateX(-50%)';
      } else if (shouldPositionLeft) {
        // Position to the left of the dropdown
        panelElement.classList.add('positioned-left');
        const leftPosition = Math.max(10, selectRect.left - panelWidth);
        panelElement.style.left = `${leftPosition}px`;
        panelElement.style.right = 'auto';
        panelElement.style.transform = 'none';
      } else {
        // Position to the right (default)
        panelElement.style.left = '0px';
        panelElement.style.right = '0px';
        panelElement.style.transform = 'none';
      }
      
      // Always position below the dropdown (never above)
      panelElement.style.top = '100%';
      panelElement.style.bottom = 'auto';
      panelElement.style.marginTop = '4px';
      panelElement.style.marginBottom = '0';
      
      // Handle vertical overflow by adjusting panel height if needed
      const spaceBelow = viewportHeight - selectRect.bottom;
      const estimatedPanelHeight = Math.min(600, panelRect.height || 500);
      
      if (spaceBelow < estimatedPanelHeight + 20) {
        // Adjust panel height to fit available space
        panelElement.style.maxHeight = `${spaceBelow - 20}px`;
        panelElement.style.overflowY = 'auto';
      }
      
      // Ensure panel is visible
      panelElement.style.zIndex = '15';
      panelElement.style.position = 'absolute';
      
      // Final check to ensure panel doesn't go off screen
      const finalRect = panelElement.getBoundingClientRect();
      if (finalRect.left < 10) {
        panelElement.style.left = '10px';
      }
      if (finalRect.right > viewportWidth - 10) {
        panelElement.style.left = `${viewportWidth - panelWidth - 10}px`;
      }
      
      console.log('Panel positioned:', {
        left: panelElement.style.left,
        top: panelElement.style.top,
        zIndex: panelElement.style.zIndex,
        position: panelElement.style.position,
        visible: panelElement.offsetParent !== null
      });
    }, 0); // Small delay to ensure DOM is updated
  }

  /**
   * Finds the parent select container element
   */
  private findSelectContainer(): HTMLElement | null {
    let element = this.elementRef.nativeElement.parentElement;
    while (element && element !== document.body) {
      if (element.classList.contains('ap-cate-select-container') ||
          element.classList.contains('ar-cate-select-container') || 
          element.classList.contains('term-select-container') ||
          element.classList.contains('customer-select-container') ||
          element.classList.contains('supplier-select-container') ||
          element.classList.contains('stock-select-container') ||
          element.classList.contains('area-select-container') ||
          element.classList.contains('purchaser-select-container') ||
          element.classList.contains('stock-category-select-container') ||
          element.classList.contains('stock-class-select-container') ||
          element.classList.contains('stock-group-select-container') ||
          element.classList.contains('stock-location-select-container')) {
        return element;
      }
      element = element.parentElement;
    }
    return null;
  }

  /**
   * Handles window resize events to reposition the panel if needed
   */
  private handleWindowResize(): void {
    if (this.visible) {
      this.autoPositionPanel();
    }
  }

  /**
   * Checks if the panel is inside a drawer/modal context
   */
  private isInsideDrawer(): boolean {
    let element = this.elementRef.nativeElement.parentElement;
    while (element && element !== document.body) {
      // Check for common drawer/modal classes
      if (element.classList.contains('ant-drawer') ||
          element.classList.contains('ant-modal') ||
          element.classList.contains('drawer') ||
          element.classList.contains('modal') ||
          element.classList.contains('nz-drawer') ||
          element.classList.contains('nz-modal') ||
          element.classList.contains('quick-scan-rule') ||
          element.classList.contains('panel-drawer')) {
        return true;
      }
      element = element.parentElement;
    }
    return false;
  }

  /**
   * Ensures form fields are properly displayed and sized
   */
  private ensureFormFieldsDisplay(panelElement: HTMLElement): void {
    // Ensure the panel content is properly sized
    const panelContent = panelElement.querySelector('.panel-content') as HTMLElement;
    if (panelContent) {
      panelContent.style.width = '100%';
      panelContent.style.height = 'auto';
      panelContent.style.overflow = 'visible';
    }

    // Ensure the form grid is properly displayed
    const formGrid = panelElement.querySelector('.form-grid') as HTMLElement;
    if (formGrid) {
      formGrid.style.display = 'grid';
      formGrid.style.gridTemplateColumns = '1fr 1fr';
      formGrid.style.gap = '16px';
      formGrid.style.width = '100%';
      formGrid.style.height = 'auto';
      formGrid.style.overflow = 'visible';
    }

    // Ensure all form rows are properly displayed (but respect hidden fields)
    const formRows = panelElement.querySelectorAll('.form-row');
    formRows.forEach((row: any) => {
      // Only set display if it's not already hidden
      if (row.style.display !== 'none') {
        row.style.display = 'block';
      }
      row.style.width = '100%';
      row.style.height = 'auto';
      row.style.overflow = 'visible';
    });

    // Ensure all form items are properly displayed
    const formItems = panelElement.querySelectorAll('nz-form-item');
    formItems.forEach((item: any) => {
      item.style.display = 'block';
      item.style.width = '100%';
      item.style.height = 'auto';
      item.style.overflow = 'visible';
    });

    // Ensure all form controls are properly displayed
    const formControls = panelElement.querySelectorAll('nz-form-control');
    formControls.forEach((control: any) => {
      control.style.display = 'block';
      control.style.width = '100%';
      control.style.height = 'auto';
      control.style.overflow = 'visible';
    });

    // Ensure all input elements are properly sized
    const inputs = panelElement.querySelectorAll('input, select, textarea');
    inputs.forEach((input: any) => {
      input.style.width = '100%';
      input.style.minWidth = '200px';
      input.style.height = '36px';
      input.style.fontSize = '14px';
      input.style.borderRadius = '6px';
      input.style.padding = '4px 11px';
      input.style.display = 'block';
      input.style.visibility = 'visible';
      input.style.opacity = '1';
    });

    // Ensure labels are properly displayed
    const labels = panelElement.querySelectorAll('nz-form-label');
    labels.forEach((label: any) => {
      label.style.display = 'flex';
      label.style.alignItems = 'center';
      label.style.gap = '4px';
      label.style.marginBottom = '8px';
      label.style.fontWeight = '500';
      label.style.color = '#262626';
      label.style.fontSize = '14px';
      label.style.visibility = 'visible';
      label.style.opacity = '1';
    });

    // Force change detection to update the UI
    this.cdr.detectChanges();

    console.log('Form fields display ensured:', {
      panelContent: !!panelContent,
      formGrid: !!formGrid,
      formRows: formRows.length,
      formItems: formItems.length,
      formControls: formControls.length,
      inputs: inputs.length,
      labels: labels.length
    });
  }

  onSelectSearch(fieldName: string, value: string): void {
    if (this.searchSubjects[fieldName]) {
      this.searchSubjects[fieldName].next(value);
    }
  }

  loadMoreSelectOptions(fieldName: string): void {
    const field = this.config.fields.find(f => f.name === fieldName);
    if (field && this.selectStates[fieldName] && this.selectStates[fieldName].hasMore && !this.selectStates[fieldName].loading) {
      this.loadSelectOptions(field, this.selectStates[fieldName].search, false);
    }
  }

  private loadSelectOptions(field: InlineAddField, search: string, isReset: boolean): void {
    if (!field.searchConfig) return;

    const state = this.selectStates[field.name];
    if (isReset) {
      state.page = 0;
      state.hasMore = true;
      state.list = [];
      state.search = search;
      state.completed = false;
    }

    state.loading = true;
    const pageSize = 20;
    const skip = state.page * pageSize;

    // Build OData URL
    let filter = 'isActive eq true';
    if (search) {
      const searchStr = search.replaceAll("'", "''");
      const searchFields = field.searchConfig.searchFields || ['code', 'name'];
      
      // Fix: Don't encode URI component here because it will be double encoded
      // when the URL is constructed. The searchStr needs to be raw text.
      // However, we still need to handle special characters properly for OData.
      // For now, let's try not encoding it here as HttpClient might handle it or the backend expects it.
      // But looking at the user's issue, the problem is likely the encoded single quotes in the filter.
      
      const searchCondition = searchFields
        .map(f => `contains(${f}, '${searchStr}')`)
        .join(' or ');
      filter = `(${filter}) and (${searchCondition})`;
    }

    // Use encodeURIComponent only for the search string value if needed, but here we are constructing
    // the filter string which will be passed as a query param. 
    // The issue might be that we are encoding the entire search string inside the filter.
    // Let's rely on the fact that we constructed the filter string safely.
    
    // Actually, looking at the working example: 
    // $filter=(contains(code, 'asd') or contains(description,'asd'))
    // And the failing one:
    // $filter=(isActive eq true) and (contains(code, 'ds') or contains(description, 'ds'))
    
    // The user mentioned: "which server returns 500."
    // This usually means the query is malformed or hitting an error on the server.
    // One difference is the 'isActive eq true' part.
    // Another potential issue is if 'isActive' property doesn't exist on the 'Terms' entity.
    
    // Let's check if we can make the 'isActive' filter optional or configurable.
    // For now, I'll update the code to only add isActive filter if explicitly configured or 
    // maybe we should check if the field supports it.
    
    // However, for Terms, it seems isActive is supported? 
    // Wait, the working URL provided by user DOES NOT have isActive filter.
    // working: $filter=(contains(code, 'asd') or contains(description,'asd'))
    // failing: $filter=(isActive eq true) and ...
    
    // So the fix is likely removing the hardcoded 'isActive eq true' or making it configurable.
    
    let baseFilter = '';
    // Only add isActive filter if not explicitly disabled in search config
    // We can add a property to searchConfig for this, but for now let's assume
    // we should only add it if we are sure.
    // Or we can rely on the fact that the working URL didn't have it.
    
    // Let's change the logic to use a configurable base filter or default to empty
    // and let the searchConfig.url handle any base filtering if needed (though url is just base path)
    // Actually, looking at the other components, they often hardcode isActive eq true.
    // But for Terms it might not be there or might be named differently?
    
    // Let's add a 'defaultFilter' to searchConfig or just default to empty if not specified?
    // The current implementation forces 'isActive eq true'.
    
    if (field.searchConfig.defaultFilter !== undefined) {
         filter = field.searchConfig.defaultFilter;
    } 
    
    // Wait, I can't easily change the interface in the same step.
    // Let's modify the code to check the URL or model type? No, that's messy.
    
    // Let's try to infer or just remove the default 'isActive eq true' 
    // and allow the config service to provide it if needed?
    // But existing configs don't provide it.
    
    // Let's look at the failure again.
    // working: api/terms/lookup...$filter=(contains...
    // failing: api/terms/lookup...$filter=(isActive eq true) and ...
    
    // It is highly likely 'isActive' is not a valid property for Terms or causing issues.
    
    // I will change the default filter to be empty string, and let the components decide.
    // BUT, most lookups probably WANT isActive=true.
    
    // Let's add `defaultFilter` to the interface in a separate edit if needed.
    // For now, I'll change the hardcoded filter.
    
    // Let's try to match the "working" URL pattern more closely.
    
    // Revised approach:
    // 1. Update InlineAddField interface to include optional `defaultFilter?: string` in searchConfig.
    // 2. Update the logic to use it.
    // 3. Update the Term config to have empty default filter.
    
    // Wait, I can do step 1 and 2 in this file.
    
    // Let's modify the logic to use a default filter only if not overridden.
    
    // Actually, simply removing 'isActive eq true' might break other lookups that rely on it.
    // I should add the capability to configure it.
    
    // Let's proceed with modifying the logic.
    
    const defaultFilter = field.searchConfig.defaultFilter !== undefined ? field.searchConfig.defaultFilter : 'isActive eq true';
    filter = defaultFilter;
    
    if (search) {
      const searchStr = search.replaceAll("'", "''");
      const searchFields = field.searchConfig.searchFields || ['code', 'name'];
      const searchCondition = searchFields
        .map(f => `contains(${f}, '${encodeURIComponent(searchStr)}')`)
        .join(' or ');
        
      if (filter) {
        filter = `(${filter}) and (${searchCondition})`;
      } else {
        filter = `${searchCondition}`;
      }
    }
    
    // And I need to update the interface in the same file.

    let url = `${field.searchConfig.url}?$top=${pageSize}&$skip=${skip}&$orderby=${field.searchConfig.orderby || 'code asc'}`;
    if (filter) {
      url += `&$filter=${filter}`;
    }

    this.http.get(url).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        const items = Array.isArray(res) ? res : (res.value || []);
        
        if (isReset) {
          state.list = items;
        } else {
          state.list = [...state.list, ...items];
        }

        state.hasMore = items.length === pageSize;
        state.page++;
        state.loading = false;
        state.completed = true;
        this.cdr.detectChanges();
      },
      error: () => {
        state.loading = false;
        state.completed = true;
        this.cdr.detectChanges();
      }
    });
  }

  getSelectOptions(field: InlineAddField): any[] {
    if (field.searchConfig && this.selectStates[field.name]) {
      return this.selectStates[field.name].list;
    }
    return field.options || [];
  }

  getSelectLoading(field: InlineAddField): boolean {
    return field.searchConfig ? (this.selectStates[field.name]?.loading || false) : false;
  }
}